// Reusable SEO head builder used by route `head()` options.
// Keeps title/description/canonical/OG/Twitter tags consistent site-wide.

export const SITE_URL = "https://mckay.steadyautogrowthdemo.com";
export const BRAND = "McKay Roofing & Solar";

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export type SEOHeadInput = {
  title: string;
  description: string;
  path: string;
  image?: string;
  keywords?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  jsonLd?: unknown[];
};

export function seoHead(input: SEOHeadInput) {
  const url = absoluteUrl(input.path);
  const image = input.image ? absoluteUrl(input.image) : undefined;
  const type = input.type ?? "website";

  const meta: Array<Record<string, string>> = [
    { title: input.title },
    { name: "description", content: input.description },
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    { name: "publisher", content: BRAND },
    { property: "og:type", content: type },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: url },
    { property: "og:site_name", content: BRAND },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
  ];

  if (input.keywords) meta.push({ name: "keywords", content: input.keywords });
  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ property: "og:image:width", content: "1200" });
    meta.push({ property: "og:image:height", content: "630" });
    meta.push({ name: "twitter:image", content: image });
  }
  if (input.author) meta.push({ name: "author", content: input.author });
  if (input.publishedTime) meta.push({ property: "article:published_time", content: input.publishedTime });
  if (input.modifiedTime) meta.push({ property: "article:modified_time", content: input.modifiedTime });

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
    scripts: (input.jsonLd ?? []).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  };
}
